import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import { listBaskets, getMediaChangeHistory, getMediaChangeSummary, markChangeReviewed } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

const FIELD_COLORS = {
    infographics: 'warning',
    aplus_content: 'primary',
    ugc_videos: 'danger',
    bullet_points: 'info',
    description: 'secondary',
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
};

const fmtDateOnly = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const FieldBadge = ({ field, label }) => (
    <span className={`badge bg-${FIELD_COLORS[field] || 'secondary'} me-1`} style={{ fontSize: '11px' }}>
        {label}
    </span>
);

const ExpandedRow = ({ changedFields }) => {
    if (!Array.isArray(changedFields) || changedFields.length === 0) {
        return <div className="text-muted small px-3 py-2">No field details available.</div>;
    }
    return (
        <div className="px-3 pb-3">
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                    <tr>
                        <th>Field</th>
                        <th>Before</th>
                        <th>After</th>
                        <th>Change</th>
                    </tr>
                </thead>
                <tbody>
                    {changedFields.map((f, i) => (
                        <tr key={i}>
                            <td>
                                <FieldBadge field={f.field} label={f.label} />
                            </td>
                            <td>{f.before != null ? f.before : '—'}</td>
                            <td>{f.after != null ? f.after : '—'}</td>
                            <td>
                                {f.type === 'content' ? (
                                    <span className="text-secondary small">Content changed</span>
                                ) : f.delta ? (
                                    <span className={f.delta.startsWith('+') ? 'text-success' : 'text-danger'} style={{ fontWeight: 600 }}>
                                        {f.delta}
                                    </span>
                                ) : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default function MediaChangeHistory() {
    const navigate = useNavigate();
    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const [baskets, setBaskets] = useState([]);
    const [history, setHistory] = useState([]);
    const [summary, setSummary] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({
        basketId: '',
        asin: '',
        dateFrom: '',
        dateTo: '',
        hasChanges: true,
        unreviewed: false,
    });

    const debounceRef = useRef(null);

    // Load baskets for filter dropdown
    useEffect(() => {
        if (!accountId) return;
        listBaskets(accountId).then(res => setBaskets(res.data || [])).catch(() => {});
    }, [accountId]);

    // Load summary on mount
    useEffect(() => {
        if (!accountId) return;
        getMediaChangeSummary(accountId, 30)
            .then(data => setSummary(data))
            .catch(() => {});
    }, [accountId]);

    const loadHistory = useCallback(async (page = 1, append = false) => {
        if (!accountId) return;
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                limit: 25,
                ...(filters.basketId && { basketId: filters.basketId }),
                ...(filters.asin && { asin: filters.asin }),
                ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
                ...(filters.dateTo && { dateTo: filters.dateTo }),
                ...(filters.hasChanges && { hasChanges: 'true' }),
                ...(filters.unreviewed && { unreviewed: 'true' }),
            };
            const res = await getMediaChangeHistory(accountId, params);
            const rows = res.data || [];
            setHistory(prev => append ? [...prev, ...rows] : rows);
            setPagination({ page, total: res.pagination?.total || 0 });
        } catch (err) {
            setError(err.message || 'Failed to load change history');
        } finally {
            setLoading(false);
        }
    }, [accountId, filters]);

    // Load on mount and filter changes (debounced for text inputs)
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => loadHistory(1, false), 300);
        return () => clearTimeout(debounceRef.current);
    }, [loadHistory]);

    const handleLoadMore = () => {
        loadHistory(pagination.page + 1, true);
    };

    const handleMarkReviewed = async (row) => {
        try {
            await markChangeReviewed(row.id);
            setHistory(prev => prev.map(r => r.id === row.id ? { ...r, reviewed_at: new Date().toISOString() } : r));
            // Update unreviewed count in summary
            setSummary(prev => prev ? { ...prev, unreviewed: Math.max(0, (prev.unreviewed || 0) - 1) } : prev);
        } catch {
            // silently fail
        }
    };

    const hasMore = history.length < pagination.total;

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="d-flex align-items-center gap-3 mb-4">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/tracking/media')}>
                        <i className="bi bi-arrow-left me-1"></i>Back
                    </button>
                    <h2 className="mb-0" style={{ fontSize: '22px', fontWeight: 700 }}>
                        <i className="bi bi-clock-history me-2 text-primary"></i>
                        Media Change History
                    </h2>
                </div>

                {/* Summary Cards */}
                {summary && (
                    <div className="row g-3 mb-4">
                        <div className="col-12 col-sm-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Changes (30 days)</div>
                                    <div style={{ fontSize: '28px', fontWeight: 700 }}>{summary.totalChanges}</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Unreviewed</div>
                                    <div style={{ fontSize: '28px', fontWeight: 700 }} className={summary.unreviewed > 0 ? 'text-danger' : ''}>
                                        {summary.unreviewed}
                                        {summary.unreviewed > 0 && <i className="bi bi-exclamation-circle ms-2" style={{ fontSize: '18px' }}></i>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Most Changed</div>
                                    <div style={{ fontSize: '16px', fontWeight: 600 }}>
                                        {summary.byField?.[0]
                                            ? <><FieldBadge field={summary.byField[0].field} label={summary.byField[0].field.replace('_', ' ')} /> ×{summary.byField[0].count}</>
                                            : <span className="text-muted">—</span>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                        <div className="row g-2 align-items-end">
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="form-label small mb-1">Basket</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={filters.basketId}
                                    onChange={e => setFilters(f => ({ ...f, basketId: e.target.value }))}
                                >
                                    <option value="">All baskets</option>
                                    {baskets.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-sm-6 col-md-2">
                                <label className="form-label small mb-1">ASIN</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="B07..."
                                    value={filters.asin}
                                    onChange={e => setFilters(f => ({ ...f, asin: e.target.value }))}
                                />
                            </div>
                            <div className="col-12 col-sm-6 col-md-2">
                                <label className="form-label small mb-1">From</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                />
                            </div>
                            <div className="col-12 col-sm-6 col-md-2">
                                <label className="form-label small mb-1">To</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                />
                            </div>
                            <div className="col-auto d-flex gap-3 align-items-center">
                                <div className="form-check mb-0">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="chkHasChanges"
                                        checked={filters.hasChanges}
                                        onChange={e => setFilters(f => ({ ...f, hasChanges: e.target.checked }))}
                                    />
                                    <label className="form-check-label small" htmlFor="chkHasChanges">Only changes</label>
                                </div>
                                <div className="form-check mb-0">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="chkUnreviewed"
                                        checked={filters.unreviewed}
                                        onChange={e => setFilters(f => ({ ...f, unreviewed: e.target.checked }))}
                                    />
                                    <label className="form-check-label small" htmlFor="chkUnreviewed">Unreviewed</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="alert alert-danger py-2 mb-3">{error}</div>
                )}

                {/* Table */}
                <div className="card border-0 shadow-sm">
                    <div className="card-body p-0">
                        {loading && history.length === 0 ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                            </div>
                        ) : history.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                <i className="bi bi-clock-history fs-1 d-block mb-2"></i>
                                No change records found.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0" style={{ fontSize: '13px' }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '24px' }}></th>
                                            <th>Date</th>
                                            <th>ASIN</th>
                                            <th>Changed Fields</th>
                                            <th>Snapshot</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map(row => {
                                            const fields = Array.isArray(row.changed_fields) ? row.changed_fields : [];
                                            const isExpanded = expandedRowId === row.id;
                                            return (
                                                <React.Fragment key={row.id}>
                                                    <tr
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                                                    >
                                                        <td className="text-center">
                                                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} text-muted`} style={{ fontSize: '11px' }}></i>
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                                                        <td>
                                                            <code style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>{row.asin}</code>
                                                        </td>
                                                        <td>
                                                            {fields.length > 0
                                                                ? fields.map((f, i) => <FieldBadge key={i} field={f.field} label={f.label} />)
                                                                : <span className="text-muted">No changes</span>
                                                            }
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateOnly(row.snapshot_date)}</td>
                                                        <td onClick={e => e.stopPropagation()}>
                                                            {row.reviewed_at ? (
                                                                <span className="text-success small">
                                                                    <i className="bi bi-check-circle-fill me-1"></i>Reviewed
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    className="btn btn-xs btn-outline-secondary"
                                                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                                                    onClick={() => handleMarkReviewed(row)}
                                                                >
                                                                    Mark Reviewed
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={6} className="p-0" style={{ background: '#f9fafb' }}>
                                                                <ExpandedRow changedFields={fields} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Load more */}
                        {hasMore && (
                            <div className="d-flex justify-content-center py-3">
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                >
                                    {loading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                                    Load more ({pagination.total - history.length} remaining)
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
